import { useState } from 'react';

import { useMutation } from '@apollo/client/react';

import type { StepListingsCompareFragment } from '@graphql/__generated__/graphql';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { INSERT_CMA_REPORTS_COMPARABLES } from '@modules/reports/cma-reports/cmaReportsQueries';
import ListingWrapper from '@modules/reports/cma-reports/step-listing-compare/ListingWrapper';
import { useAnalytics } from '@shared/analytics';
import { ANALYTICS_EVENTS } from '@shared/analytics/constants';
import { getBaseCmaEventProperties } from '@shared/analytics/posthog-helpers';
import { useSet } from '@shared/hooks/useSet';

import { replaceCmaReportComparableListings } from './cacheUtils';
import { ListingsPicker } from './ListingsPicker';

const AggregatesListings = ({
  onClose,
  data,
  refetch,
  cmaReportId,
}: {
  onClose: () => void;
  data: StepListingsCompareFragment;
  refetch: any;
  cmaReportId: string;
}) => {
  const { me } = useAppData();
  const { track } = useAnalytics();
  const listings = data?.comparable_listings ?? [];
  const selection = useSet<string | number>(
    listings.map(listing => listing.aggregates_db__merged_listings_clusters_id),
  );

  const [tab, setTab] = useState<'automatic' | 'manual'>('manual');

  const handleSelect = (id: string | number) => {
    if (selection.has(id)) {
      return selection.delete(id);
    }
    return selection.add(id);
  };

  const [insertComparable, { loading: insertingComparable }] = useMutation(
    INSERT_CMA_REPORTS_COMPARABLES,
  );

  const handleSave = () => {
    insertComparable({
      variables: {
        id: cmaReportId,
        objects: selection.values().map(id => ({
          aggregates_db__merged_listings_clusters_id: id,
          cma_report_id: cmaReportId,
        })),
      },
      update: (cache, { data: updatedData }) =>
        replaceCmaReportComparableListings(cache, cmaReportId, updatedData),
      onCompleted: savedData => {
        const source = data?.report_source;
        const propertyId = data?.property?.id;
        const leadId = data?.lead?.id;

        const count =
          savedData.insert_cma_reports_comparables?.returning.length;

        const baseProperties = getBaseCmaEventProperties(me, cmaReportId);
        if (baseProperties) {
          track(ANALYTICS_EVENTS.CMA_LISTINGS_MANUAL_SAVE, {
            ...baseProperties,
            source: source ?? 'unknown',
            ...(count !== undefined && { count }),
            ...(propertyId && { property_id: propertyId }),
            ...(leadId && { lead_id: leadId }),
          });
        }
        onClose();
        refetch();
      },
    });
  };

  return (
    <ListingsPicker
      onClose={onClose}
      tab={tab}
      onTabChange={setTab}
      selectionCount={selection.size}
      onClear={selection.clear}
      onSave={handleSave}
      saving={insertingComparable}
    >
      <ListingWrapper
        mode={tab}
        data={data}
        selection={selection}
        onSelect={handleSelect}
      />
    </ListingsPicker>
  );
};

export default AggregatesListings;
